import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/PageReceipt.css';
import ButtonRegular from './ui/ButtonRegular';

function PageReceipt() {
	const location = useLocation();
	const navigate = useNavigate();

	const order = location.state?.order;
	const delivery = location.state?.delivery || null;

	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [regulationAccepted, setRegulationAccepted] = useState(false);

	const isMounted = useRef(true);

	const token = localStorage.getItem('authToken');
	const API_URL = process.env.REACT_APP_API_URL || '';

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handlePayment = useCallback(async () => {
		if (!order) return;
		if (loading || !regulationAccepted) return;

		setLoading(true);
		setError('');

		try {
			const res = await axios.post(
				`${API_URL}/api/payment/p24/create-transaction`,
				{
					orderNumber: order.orderNumber || order._id,
					regulationAccept: true,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
					timeout: 15000,
				}
			);

			if (!res.data?.success) {
				throw new Error(
					res.data?.message || 'Nie udało się utworzyć płatności'
				);
			}

			if (!res.data?.redirectUrl) {
				throw new Error('Brak linku do płatności');
			}

			window.location.href = res.data.redirectUrl;
		} catch (err) {
			console.error('P24 payment error:', err);

			if (!isMounted.current) return;

			setError(
				err.response?.data?.message ||
					err.message ||
					'Wystąpił problem z inicjacją płatności'
			);
			setLoading(false);
		}
	}, [API_URL, order, token, loading, regulationAccepted]);

	// 🔹 Dopiero po wszystkich hookach robimy warunkowy return
	if (!order) {
		return (
			<div className="form-container">
				<div className="form-content">
					<p>Brak danych zamówienia.</p>
					<div className="summary-actions">
						<ButtonRegular onClick={() => navigate('/')}>
							Powrót do strony głównej
						</ButtonRegular>
					</div>
				</div>
			</div>
		);
	}

	const VAT_RATE = 0.23;

	const cartWithCalculations = (order.items || []).map((item, index) => {
		const brutto = item.price;
		const netto = brutto / (1 + VAT_RATE);

		return {
			lp: index + 1,
			name: item.name,
			quantity: item.quantity,
			priceNetto: netto.toFixed(2),
			priceBrutto: brutto.toFixed(2),
			valueNetto: (netto * item.quantity).toFixed(2),
			valueBrutto: (brutto * item.quantity).toFixed(2),
		};
	});

	const totalNetto = cartWithCalculations
		.reduce((sum, i) => sum + parseFloat(i.valueNetto), 0)
		.toFixed(2);

	const totalBrutto = cartWithCalculations
		.reduce((sum, i) => sum + parseFloat(i.valueBrutto), 0)
		.toFixed(2);

	const deliveryPrice = delivery?.price || 0;

	const totalWithDelivery = (
		parseFloat(totalBrutto) + deliveryPrice
	).toFixed(2);

	return (
		<div className="form-container">
			<div className="form-content">
				<h2 className="form-title">
					Potwierdzenie złożenia zamówienia
				</h2>

				<p>
					Numer zamówienia:{' '}
					<strong>{order.orderNumber || order._id}</strong>
				</p>

				<p>
					Data:{' '}
					{order.createdAt
						? new Date(order.createdAt).toLocaleString()
						: ''}
				</p>

				<table className="summary-table">
					<thead>
						<tr>
							<th>LP</th>
							<th>Nazwa</th>
							<th>Ilość</th>
							<th>Cena netto</th>
							<th>Cena brutto</th>
							<th>Wartość netto</th>
							<th>Wartość brutto</th>
						</tr>
					</thead>
					<tbody>
						{cartWithCalculations.map(item => (
							<tr key={item.lp}>
								<td>{item.lp}</td>
								<td>{item.name}</td>
								<td>{item.quantity}</td>
								<td>{item.priceNetto} PLN</td>
								<td>{item.priceBrutto} PLN</td>
								<td>{item.valueNetto} PLN</td>
								<td>{item.valueBrutto} PLN</td>
							</tr>
						))}
					</tbody>
				</table>

				{delivery && (
					<div className="summary-section" style={{ marginTop: '20px' }}>
						<h3>Dostawa</h3>
						<p>
							{delivery.label} – {delivery.price.toFixed(2)} PLN
						</p>
					</div>
				)}

				<div className="summary-total" style={{ marginTop: '12px' }}>
					<p>Suma netto: {totalNetto} PLN</p>
					<p>Suma brutto: {totalBrutto} PLN</p>
					{delivery && (
						<p>Dostawa: {delivery.price.toFixed(2)} PLN</p>
					)}
					<p className="summary-pay">
						W sumie do zapłaty: {totalWithDelivery} PLN
					</p>
				</div>

				<div className="summary-section" style={{ marginTop: '20px' }}>
					<p>
						Uprzejmie informujemy, że termin na opłacenie zamówienia wynosi 7 dni.
						Po tym czasie nieopłacone zamówienia są automatycznie anulowane,
						aby produkty mogły wrócić do sprzedaży dla innych klientów.
					</p>
				</div>

				{error && <p className="form-error">{error}</p>}

				<div className="summary-actions">
					<div className="payment-box">
						<div className="summary-terms">
							<input
								type="checkbox"
								checked={regulationAccepted}
								onChange={(e) =>
									setRegulationAccepted(e.target.checked)
								}
							/>
							<label>
								Oświadczam, że zapoznałem się z{' '}
								<a
									href="https://www.przelewy24.pl/regulamin"
									target="_blank"
									rel="noopener noreferrer"
								>
									regulaminem
								</a>{' '}
								oraz{' '}
								<a
									href="https://www.przelewy24.pl/obowiazek-informacyjny-rodo-platnicy"
									target="_blank"
									rel="noopener noreferrer"
								>
									obowiązkiem informacyjnym
								</a>{' '}
								serwisu Przelewy24.
							</label>
						</div>

						{!regulationAccepted && (
							<p className="p24-warning">
								Aby przejść do płatności, zaakceptuj regulamin Przelewy24.
							</p>
						)}

						<ButtonRegular
							onClick={handlePayment}
							disabled={loading || !regulationAccepted}
							className={`p24-button ${
								loading || !regulationAccepted ? 'disabled' : ''
							}`}
						>
							{loading ? (
								'Przekierowanie do płatności…'
							) : (
								<>
									<img
										src="/p24/Przelewy24_logo.png"
										alt="Przelewy24"
										className="p24-logo-img"
									/>
									<span>Opłać zamówienie</span>
								</>
							)}
						</ButtonRegular>
					</div>

					<ButtonRegular onClick={() => navigate('/')}>
						Powrót do strony głównej
					</ButtonRegular>
				</div>
			</div>
		</div>
	);
}

export default PageReceipt;